import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as ProductIcon } from 'assets/icons/product.svg';
import { ReactComponent as WarehouseIcon } from 'assets/icons/warehouse.svg';
import clsx from 'clsx';
import { NO_LOCATION_KEY } from 'constants/general';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';

import type IScenarioShowcaseEntities from './interfaces/IScenarioShowcaseEntities';

import styles from './scss/ScenarioShowcaseEntities.module.scss';

const getEntityIcon = (type: string): React.ReactNode => {
  if (type === VALUE_CHAIN_UNIT_TYPES.SUPPLIER) {
    return <WarehouseIcon />;
  }

  return <ProductIcon />;
};

function ScenarioShowcaseEntities({
  totalUnits,
  isExpanded,
  groups,
  onToggle,
  stopTogglePropagation = false,
}: IScenarioShowcaseEntities): React.JSX.Element | null {
  const { t } = useTranslation();

  if (totalUnits === 0) {
    return null;
  }

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopTogglePropagation) {
      event.stopPropagation();
    }

    onToggle();
  };

  return (
    <div className={styles.entitiesSection}>
      <button
        type="button"
        className={clsx(styles.entitiesHeader, {
          [styles.entitiesHeaderExpanded]: isExpanded,
        })}
        onClick={handleToggle}
        aria-expanded={isExpanded}
      >
        <span className={styles.entitiesTitle}>
          {t('valueChainMap.selectedEntitiesLabel')}
          {' '}
          <span className={styles.entitiesCount}>
            (
            {totalUnits}
            )
          </span>
        </span>
        <ChevronIcon
          className={clsx(styles.chevronIcon, {
            [styles.expanded]: isExpanded,
          })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.entitiesList}>
          {groups.map(({ countryKey, units }) => {
            const groupLabel = countryKey === NO_LOCATION_KEY
              ? `${t('valueChainMap.noLocation')} (${units.length}):`
              : `${countryKey} (${units.length}):`;

            return (
              <div key={countryKey} className={styles.countryGroup}>
                <span className={styles.countryLabel}>{groupLabel}</span>
                <div className={styles.chips}>
                  {units.map((entity) => (
                    <div
                      key={entity.id}
                      className={clsx(styles.chip, {
                        [styles.chipWarning]: entity.isMissing,
                      })}
                      data-type={entity.type}
                    >
                      <span
                        className={clsx(styles.chipIcon, {
                          [styles.warningIcon]: entity.isMissing,
                        })}
                      >
                        {getEntityIcon(entity.type)}
                      </span>
                      <span className={styles.chipName}>{entity.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default ScenarioShowcaseEntities;
